/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import { parseTargets } from '../../content/info/content-info-parser';
import UniversalController from './universal-controller';

/**
 * @import { IAnalyticsTracker } from '../../analytics'
 * @import { ViewerManagerController } from '.'
 * @import { ContentInfo } from '../../content/info/content-info'
 * @import { ContentTaskCronFactory } from '../../content/task/cron/content-task-cron-factory'
 * @import { Logger } from '../../logger/logger'
 * @import { ViewerRegistry } from '../registry'
 * @import UnixTimestamp from '../../unix-timestamp/unix-timestamp'
 * @import { ContentControllerFactory } from '../../content/controller'
 */

/**
 * @private
 * @typedef {Record<string, *>} AppManager TODO #555
 */

const unexpectedContentInfo = new Error(
  'ModernController created with unexpected ContentInfo.'
);

const apiVersion = '0.1.0';

/**
 * @implements {ViewerManagerController}
 */
export default class ModernController extends UniversalController {
  /**
   * @param {IAnalyticsTracker} analyticsTracker
   * @param {AppManager} appManager
   * @param {ContentControllerFactory} contentControllerFactory
   * @param {ContentInfo} contentInfo
   * @param {ContentTaskCronFactory} contentTaskCronFactory
   * @param {HTMLElement} el
   * @param {Logger} logger
   */
  constructor(
    analyticsTracker,
    appManager,
    contentControllerFactory,
    contentInfo,
    contentTaskCronFactory,
    el,
    logger
  ) {
    super(
      analyticsTracker,
      appManager,
      contentControllerFactory,
      contentInfo,
      contentTaskCronFactory,
      el,
      logger
    );
    if (contentInfo.visualController.type !== 'ModernController') {
      throw unexpectedContentInfo;
    }

    /** @type {(() => void) | undefined} */
    this.destroySuperEngineController = undefined;

    this.receiveMessage = this.receiveMessage.bind(this);

    window.addEventListener('message', this.receiveMessage);
  }

  /**
   * TODO: Tuto funkci nelze volat v testovacích souborech,
   * protože by padala na unresolved reference 'js-api'.
   * Je to způsobené tím, že js-api se builduje jako první a následný
   * build je odkazován ve webpacku v sekci resolve->alias.
   * Tím, že karma-webpack neumožňuje zadat konfiguraci webpacku jako pole,
   * tak nelze docílit toho, aby se před spuštěním testů prvně pustil build
   * js-api a následně se spustily testy.
   * @param {string} script
   * @returns {Promise<string>}
   */
  async htmlWithScript(script) {
    // Importujeme pouze pokud je potřeba.
    // @ts-ignore Modul vzniká až dynamicky při buildu.
    const jsApi = await import('js-api');
    return `<!DOCTYPE html>
<html>
  <head>
    <script>${jsApi.default}</script>
    <script>${script}</script>
  </head>
  <body></body>
</html>`;
  }

  /**
   * @param {MessageEvent} event
   * @param {string} id
   * @param {unknown} data
   */
  sendConfirmation(event, id, data) {
    /** @type {WindowProxy} */ (event.source)?.postMessage(
      {
        type: 'player-lighter-api',
        detail: {
          id,
          command: 'confirm',
          data
        }
      },
      // Origin * musí být kvůli tomu,
      // že iframe se srcdoc má origin null,
      // ale null nelze poslat.
      '*'
    );
  }

  /**
   * TODO: vyčlenit do centrálního místa
   * @param {MessageEvent} event
   */
  async receiveMessage(event) {
    const { detail, type } = event.data;
    if (type !== 'player-lighter-api' || !detail) {
      return;
    }
    const { command, id, params } = detail;
    if (command === 'kiwi.screen.switchTarget') {
      let data;
      try {
        const displayId = params[0] ?? '';
        const target = params[1] ?? '';
        const { fromTarget, toTarget } = parseTargets(displayId, target);
        await super.changeContent(fromTarget, toTarget);
      } catch (err) {
        data = {
          error: /** @type {Error} */ (err).toString()
        };
      }
      this.sendConfirmation(event, id, data);
    } else if (command === 'kiwi.getApiVersion') {
      this.sendConfirmation(event, id, apiVersion);
    }
  }

  /**
   * Nastaví novou sadu aktuálně přehrávaných obsahových úloh.
   * Nepřehrává se více úloh zároveň, ale mohou se střídat v čase,
   * což zajišťuje CRON, který se zároveň spustí.
   * @type {ViewerManagerController['start']}
   */
  async start() {
    if (this.contentInfo.visualController.type !== 'ModernController') {
      throw unexpectedContentInfo;
    }
    // Prvně se spustí obsah, zaregistrují se podmínky. A následně se přidá JS kód.
    await super.start();

    const { superengineJs } = this.contentInfo.visualController;
    if (superengineJs !== '') {
      /** @type {HTMLIFrameElement} */
      const iframe = document.createElement('iframe');
      iframe.id = 'superengine';
      const sandboxOptions = ['allow-scripts'];
      if (typeof iframe.sandbox === 'string') {
        iframe.sandbox = sandboxOptions.join(' ');
      } else {
        iframe.sandbox.add(...sandboxOptions);
      }
      iframe.width = '0';
      iframe.height = '0';

      iframe.srcdoc = await this.htmlWithScript(superengineJs);
      document.body.appendChild(iframe);
      this.destroySuperEngineController = () => {
        document.body.removeChild(iframe);
      };
    }
  }

  /**
   * Odebere sadu aktuálně přehrávaných obsahových úloh
   * a zastaví jejich plánování.
   * @type {ViewerManagerController['stop']}
   */
  async stop() {
    window.removeEventListener('message', this.receiveMessage);
    if (this.destroySuperEngineController) {
      this.destroySuperEngineController();
      this.destroySuperEngineController = undefined;
    }
    await super.stop();
  }
}
